import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { AppShell, PageHeader } from '@/components/AppShell';
import { toast } from 'sonner';
import {
  PanelLeftOpen,
  Layers,
  Loader2,
  Square,
  Play,
  Smartphone,
  ExternalLink,
  Clock,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  listActiveRuns,
  listSessions,
  stopGroupAgents,
  type ActiveRun,
  type AccountIssue,
} from '@/services/vmos-agentic-group-api';
import { getStatusBadge } from '@/components/vmos/utils';
import { RunAttributionBadge } from '@/components/vmos/RunAttributionBadge';
import { useUser } from '@/hooks/useUser';

function formatDuration(seconds: number | null): string {
  if (!seconds) return '—';
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function IssueBadge({
  issues,
  failed,
}: {
  issues?: Record<string, AccountIssue>;
  failed?: number | null;
}) {
  const entries = issues ? Object.entries(issues) : [];
  if (entries.length === 0 && !failed) return null;
  const label = entries.length
    ? `${entries.length} account issue${entries.length > 1 ? 's' : ''}`
    : `${failed} failed`;
  const title = entries.length
    ? entries
        .map(([pad, i]) => `${pad}: ${i.issue_type ?? 'issue'}${i.detail ? ` — ${i.detail}` : ''}`)
        .join('\n')
    : `${failed} device(s) failed`;
  return (
    <Badge
      variant="outline"
      className="text-[10px] gap-1 border-amber-500/40 text-amber-600 shrink-0"
      title={title}
    >
      <AlertTriangle className="h-3 w-3" />
      {label}
    </Badge>
  );
}

function ActiveRunCard({
  run,
  onOpen,
  onStop,
  stopping,
  meId,
}: {
  run: ActiveRun;
  onOpen: () => void;
  onStop: () => void;
  stopping: boolean;
  meId?: string;
}) {
  const devices = Object.entries(run.devices);
  const doneCount = devices.filter(([, d]) =>
    d.status === 'completed' || d.status === 'failed' || d.status === 'stopped'
  ).length;

  return (
    <div className="border rounded-lg bg-card hover:border-primary/40 transition-colors">
      <div className="flex items-center gap-3 px-4 py-3 border-b">
        <span className="relative flex h-3 w-3 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold truncate">{run.task}</h3>
          <p className="text-xs text-muted-foreground truncate">
            {run.persona || 'No persona'} · started {new Date(run.started_at * 1000).toLocaleString()}
          </p>
        </div>
        <RunAttributionBadge run={run} meId={meId} />
        <IssueBadge issues={run.account_issues} />
        <span className="text-xs text-muted-foreground font-mono tabular-nums shrink-0">
          {doneCount}/{devices.length} done
        </span>
        <Button variant="ghost" size="sm" onClick={onOpen}>
          <ExternalLink className="h-4 w-4 mr-1" /> Open
        </Button>
        <Button variant="destructive" size="sm" onClick={onStop} disabled={stopping}>
          {stopping ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Square className="h-3 w-3 mr-1" />}
          Stop
        </Button>
      </div>

      <div className="p-3 space-y-1.5">
        {devices.length === 0 && (
          <p className="text-xs text-muted-foreground px-1">No devices attached yet.</p>
        )}
        {devices.map(([padId, dev]) => (
          <div
            key={padId}
            className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-md bg-muted/40"
          >
            <Smartphone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="font-medium truncate min-w-0 flex-1" title={padId}>
              {dev.device_name || padId.slice(0, 8)}
            </span>
            <span className="font-mono tabular-nums text-muted-foreground shrink-0">
              {dev.step}/{dev.max_steps}
            </span>
            {getStatusBadge(dev.status)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SessionsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: user } = useUser();
  const meId = user?.id;

  const { data: activeData, isLoading: activeLoading, isError: activeError } = useQuery({
    queryKey: ['active-runs'],
    queryFn: listActiveRuns,
    refetchInterval: 5000,
  });

  const { data: sessionsData, isLoading: historyLoading, isError: historyError } = useQuery({
    queryKey: ['sessions-list'],
    queryFn: () => listSessions({ limit: 50 }),
    refetchInterval: 10000,
  });

  const activeRuns = activeData?.runs ?? [];

  const stopMutation = useMutation({
    mutationFn: (groupId: string) => stopGroupAgents(groupId),
    onSuccess: () => {
      toast.success('Run stopped');
      qc.invalidateQueries({ queryKey: ['active-runs'] });
      qc.invalidateQueries({ queryKey: ['sessions-list'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const history = (sessionsData?.sessions ?? []).filter(
    (s) => s.status === 'completed' || s.status === 'completed_with_issues' || s.status === 'failed' || s.status === 'stopped'
  );
  const activeIds = new Set(activeRuns.map((r) => r.group_id));
  const historyExcludingActive = history.filter((s) => !activeIds.has(s.group_id));

  return (
    <AppShell>
      <div className="flex flex-col h-full">
        <PageHeader>
            <SidebarTrigger>
              <PanelLeftOpen className="h-4 w-4" />
            </SidebarTrigger>
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold leading-tight">Sessions</h1>
              <p className="text-xs text-muted-foreground">
                All agentic-group runs. Active sessions stay live here even if you close the tab — open one to dive in.
              </p>
            </div>
          </PageHeader>

          <div className="flex-1 overflow-auto px-6 py-6 space-y-8">
            {/* Active */}
            <section className="space-y-3">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                Active ({activeRuns.length})
              </h2>
              {activeLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm py-8">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading active sessions…
                </div>
              ) : activeError ? (
                <div className="flex items-center gap-2 text-destructive text-sm py-8">
                  <AlertCircle className="h-4 w-4" /> Failed to load active sessions.
                </div>
              ) : activeRuns.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-12 text-center border rounded-lg">
                  <Play className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm font-medium">No active sessions</p>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    Start agents from the Runs page and they'll appear here — keep running even if you navigate away.
                  </p>
                  <Button size="sm" variant="outline" onClick={() => navigate('/runs')}>
                    Go to Runs
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeRuns.map((run) => (
                    <ActiveRunCard
                      key={run.group_id}
                      run={run}
                      meId={meId}
                      onOpen={() => navigate(`/runs/${run.group_id}`)}
                      onStop={() => stopMutation.mutate(run.group_id)}
                      stopping={stopMutation.isPending && stopMutation.variables === run.group_id}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* History */}
            <section className="space-y-3">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                History ({historyExcludingActive.length})
              </h2>
              {historyLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm py-8">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
                </div>
              ) : historyError ? (
                <div className="flex items-center gap-2 text-destructive text-sm py-8">
                  <AlertCircle className="h-4 w-4" /> Failed to load history.
                </div>
              ) : historyExcludingActive.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center border rounded-lg">
                  <Layers className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm font-medium">No past sessions</p>
                  <p className="text-xs text-muted-foreground">Completed runs will show up here.</p>
                </div>
              ) : (
                <div className="border rounded-lg divide-y">
                  {historyExcludingActive.map((session) => (
                    <div
                      key={session.group_id}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{session.task}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {new Date(session.started_at).toLocaleString()}
                          {' · '}
                          {session.device_count} device{session.device_count === 1 ? '' : 's'}
                          {' · '}
                          {formatDuration(session.duration_seconds)}
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        <IssueBadge issues={session.account_issues} failed={session.devices_failed} />
                        <RunAttributionBadge run={session} meId={meId} />
                        {getStatusBadge(session.status)}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => navigate(`/runs/${session.group_id}`)}
                      >
                        <ExternalLink className={cn('h-4 w-4 mr-1')} /> View
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
    </AppShell>
  );
}
